import type { Turn } from './turns'

/**
 * Keeping Pearl's conversation across a navigation and a refresh.
 *
 * WHY THIS EXISTS
 *
 * The conversation lived in component state, so leaving the Pearl tab threw it
 * away — and leaving the Pearl tab is exactly what she does when Pearl has just
 * told her how to do something. She would come back to an empty screen and a
 * greeting, having lost the instructions she went to follow.
 *
 * WHY IT IS A SEPARATE FILE
 *
 * There is no browser harness in this project, so the rules about what is worth
 * keeping, how much of it, and when it has gone stale are plain functions here
 * rather than conditions buried in an effect. What is left uncovered: that
 * `localStorage` actually survives a reload. What IS covered: every decision
 * about what to write, what to accept back, and what to throw away — which is
 * the half that can put nonsense on screen.
 */

/** Where it lives. Namespaced, because this is Renee's own browser profile. */
export const PEARL_KEY = 'agentcare.pearl.chat'

/**
 * How long a conversation is still worth restoring.
 *
 * Twelve hours: a conversation from this morning is context, one from last week
 * is a stranger's. Long enough to survive lunch, a reboot and an accidental
 * close; short enough that she is never greeted by a half-finished exchange she
 * has no memory of.
 */
export const MAX_AGE_MS = 12 * 60 * 60 * 1000

/**
 * How much of it to keep.
 *
 * `localStorage` throws `QuotaExceededError` when it is full, and a long
 * conversation about a roster of 789 people is not small. The cap is on
 * CHARACTERS rather than turns because one streamed answer can be longer than
 * twenty questions.
 *
 * Trimming from the front, never the back. The most recent exchange is the one
 * she left to go and act on.
 */
export const MAX_CHARS = 60_000

/**
 * What a `view` is assumed to cost on top of a turn's words.
 *
 * A guess, and named so that it reads as one. It is a rough stand-in for the
 * JSON a "Show me" offer serialises to; a view with an unusually long filter
 * list will overshoot it. The cap is therefore a brake on ordinary growth, not
 * a guarantee about the size of the written string — if it ever needs to be a
 * guarantee, measure `JSON.stringify` instead of counting characters.
 */
export const VIEW_ALLOWANCE = 200

export type Stored = { at: number; turns: Turn[] }

/** What came back, and when the conversation it belongs to began. */
export type Restored = { turns: Turn[]; at: number }

/** Is this shaped like a turn we could render? */
function isTurn(x: unknown): x is Turn {
  if (typeof x !== 'object' || x === null) return false
  const t = x as Record<string, unknown>
  if (t.role !== 'user' && t.role !== 'assistant') return false
  /**
   * A string, not merely present. A number here reaches `emphasisParts`, which
   * calls `.split` on it — an uncaught throw during render, and the whole
   * screen goes with it.
   */
  if (typeof t.content !== 'string') return false
  /**
   * A `view` is an OFFER to put a group of agents on screen, and the button
   * that accepts it is built straight from these fields. A malformed one
   * restored from storage would be a button that either does nothing or filters
   * to something nobody asked for — so a turn carrying a bad view keeps its
   * words and loses its button, rather than being dropped or trusted.
   */
  return true
}

/**
 * Is this offer safe to put a "Show me" button on?
 *
 * EXPORTED, and the live stream in `pearlchat.tsx` uses this same function.
 * They used to be two checks with a comment claiming they were one: the stream
 * accepted anything with a `filters` array, storage additionally demanded a
 * `label`. So a view frame with no label rendered live as a card with an empty
 * title and a working button, and lost the button entirely after a refresh —
 * the same data behaving two ways, which is the shape of thing this project
 * has now found in its own code more than once.
 */
export function usableView(v: unknown): boolean {
  if (typeof v !== 'object' || v === null) return false
  const view = v as Record<string, unknown>
  return Array.isArray(view.filters) && typeof view.label === 'string'
}

/**
 * Turns, trimmed from the front until they fit.
 *
 * A leading assistant turn with no question above it is dropped — a reply to a
 * question that has been trimmed away reads as Pearl volunteering something
 * unprompted, and on a screen whose empty state is a greeting that is genuinely
 * confusing.
 *
 * **WITH ONE EXCEPTION, and it is a real one rather than an oversight.** When
 * the last surviving turn IS that reply — a single answer longer than the whole
 * budget — it is kept. Two rules were fighting: "never leave an orphan" and
 * "never trim to nothing", and the first was winning and deleting the answer
 * she had gone away to read. An answer with no question above it is mildly odd;
 * losing the answer is not. Asserted, so nobody tidies it away.
 */
export function trimTurns(turns: Turn[], budget: number = MAX_CHARS): Turn[] {
  const size = (t: Turn) => t.content.length + (t.view ? VIEW_ALLOWANCE : 0)
  const out = [...turns]
  let total = out.reduce((n, t) => n + size(t), 0)
  /**
   * `out.length > 1`, NOT `> 0`, and that one character is the difference
   * between trimming and deleting.
   *
   * Ask Pearl to describe a large group and one reply can be longer than the
   * whole budget. Trimming to nothing then made `toStore` write null, which
   * makes the provider REMOVE the key — so a single long answer wiped the
   * morning's instructions as well as itself, and the next visit opened on a
   * greeting. That is the exact loss this whole feature exists to prevent,
   * caused by the mechanism meant to prevent it.
   */
  while (out.length > 1 && total > budget) {
    total -= size(out[0])
    out.shift()
  }
  /**
   * A last turn that is on its own and still too big is CUT, not dropped. Half
   * an answer she can scroll beats no answer and no question.
   */
  if (out.length === 1 && size(out[0]) > budget) {
    out[0] = { ...out[0], content: out[0].content.slice(0, Math.max(0, budget - (out[0].view ? VIEW_ALLOWANCE : 0))) }
  }
  /**
   * A reply with no question above it reads as Pearl volunteering something
   * unprompted, so a leading assistant turn is dropped — but only while
   * something else remains.
   *
   * `> 1`, not `> 0`, and that was a real collision between two rules: with
   * `> 0` this loop ate the single surviving turn that the truncation above had
   * just gone to the trouble of keeping, and the conversation was wiped after
   * all. Rather that than nothing: an answer with no question above it is
   * slightly odd, and losing the answer entirely is not.
   */
  while (out.length > 1 && out[0].role === 'assistant' && !out[0].view) out.shift()
  return out
}

/**
 * What to write, or null when there is nothing worth writing.
 *
 * An empty conversation writes null rather than `[]` so that the caller REMOVES
 * the key instead of storing an empty one. A stored empty conversation is
 * indistinguishable from a fresh one to every reader, and leaving it behind
 * means the next visit restores "nothing" over a greeting that was about to be
 * shown anyway.
 *
 * `startedAt` is WHEN THE CONVERSATION BEGAN, not now. Stamping `now` on every
 * write re-armed the expiry on every page load, so `MAX_AGE_MS` silently became
 * "twelve hours since the app was last open" — and for anyone who opens the
 * dashboard daily, which is the entire intended use, a conversation would never
 * expire at all. Monday's exchange would still be sitting there on Friday.
 */
export function toStore(turns: Turn[], startedAt: number): string | null {
  const kept = trimTurns(turns)
  if (kept.length === 0) return null
  return JSON.stringify({ at: startedAt, turns: kept } satisfies Stored)
}

/**
 * What to restore from whatever was in storage.
 *
 * Everything about this is defensive on purpose. The value is read back from a
 * browser profile that another version of this app wrote, that the user may
 * have edited, and that may have been truncated by a full disk. The only
 * acceptable failure is an empty conversation.
 */
export function fromStore(raw: string | null, now: number, maxAge: number = MAX_AGE_MS): Restored {
  const nothing: Restored = { turns: [], at: 0 }
  if (!raw) return nothing
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return nothing
  }
  /**
   * `null` is an object to `typeof`, and `JSON.parse('null')` produces it. This
   * check is what that clause is for; without it the property reads below throw
   * and take the screen down on a stored value of four characters.
   */
  if (typeof parsed !== 'object' || parsed === null) return nothing
  const s = parsed as Record<string, unknown>
  /**
   * A `number`, not merely present. `{"at":"0"}` makes every comparison below
   * `NaN`-false, so a string timestamp sails through both the future check and
   * the age check and restores a conversation of any age.
   */
  if (typeof s.at !== 'number' || Number.isNaN(s.at)) return nothing
  /**
   * A stored time in the FUTURE is not restored either. It means the clock has
   * moved backwards — a timezone change, a corrected clock, a copied profile —
   * and `now - at` being negative would sail through an age check and restore
   * something arbitrarily old. Anything more than a minute ahead is refused.
   */
  if (s.at - now > 60_000) return nothing
  if (now - s.at > maxAge) return nothing
  if (!Array.isArray(s.turns)) return nothing

  const turns: Turn[] = []
  for (const raw of s.turns) {
    if (!isTurn(raw)) continue
    const t = raw as Turn & { view?: unknown }
    turns.push(usableView(t.view) ? (t as Turn) : { role: t.role, content: t.content })
  }
  /**
   * An answer that was still streaming when the tab closed was stored empty.
   * Restored, it renders as a blank bubble that never fills in.
   *
   * **UNLESS IT CARRIES AN OFFER.** `dropEmptyPlaceholder` in `turns.ts` keeps
   * exactly this turn, for exactly this reason: a reply can legitimately be
   * nothing but a "Show me" card. Without the `!t.view` clause the two files
   * disagreed about the same turn — the live screen kept the button, a refresh
   * threw it away — and each had a green test defending its own half.
   */
  const settled = turns.filter((t) => !(t.role === 'assistant' && t.content === '' && !t.view))
  /**
   * Trimmed on the way IN as well as on the way out. The blob may have been
   * written by a build with a larger cap, or by a build before there was one.
   */
  return { turns: trimTurns(settled), at: s.at }
}
